/**
 * État dérivé d'un événement pour /b/[slug] :
 *  - isCompleted / isEventEnded / eventEndedLabel
 *  - formattedDate / formattedEnd
 */
import { toDayKey } from "@donoor/utils/dates";
import type { Event } from "~/types/event";
import { fuseauDe } from "~/utils/heureAssociation";

export function useEventStatus(event: Ref<Event | null>) {
  const { t } = useI18n();
  const { formatDate } = useFormatLangue();
  const isCompleted = computed(() => event.value?.status === "completed");

  // Clôturé (status 'closed') : événement visible/consultable mais billetterie
  // fermée (plus de nouvelle vente). Distinct de terminé/annulé : ce n'est PAS
  // inclus dans isEventEnded. Karim 2026-06-27.
  const isClosed = computed(() => event.value?.status === "closed");

  // Passé : la date de l'événement est dépassée (calcul de l'API, à l'heure
  // de l'association) sans que l'association l'ait marqué terminé. La
  // billetterie en ligne est fermée (Karim 2026-10-09).
  const isPast = computed(() => !!event.value?.evenement_passe);

  const isEventEnded = computed(() => {
    if (!event.value) return false;
    const status = event.value.status;
    return status === "completed" || status === "canceled" || isPast.value;
  });

  const eventEndedLabel = computed(() => {
    if (!event.value) return "";
    if (event.value.status === "canceled") return t("billetterie.useEventStatus.etatAnnule");
    return event.value.status === "completed" ? t("billetterie.useEventStatus.etatTermine") : t("billetterie.useEventStatus.etatPasse");
  });

  // Fuseau de l'ASSOCIATION (`event.timezone`, Paris par défaut) : un gala à
  // 19:00 en Martinique s'affiche 19:00, pas 01:00 le lendemain (lot 2,
  // 2026-09-25). « Même jour » se juge aussi chez elle.
  const fuseau = computed(() => fuseauDe(event.value));

  const formattedDate = computed(() => {
    if (!event.value?.start_date) return "";
    const timeZone = fuseau.value;
    const date = formatDate(event.value.start_date, { format: "weekday-long", timeZone });
    const time = formatDate(event.value.start_date, { format: "time", timeZone });
    return t("billetterie.useEventStatus.dateDebut", { date, heure: time });
  });

  const formattedEnd = computed(() => {
    if (!event.value?.end_date) return null;
    const timeZone = fuseau.value;
    const start = event.value.start_date;
    const end = event.value.end_date;
    const sameDay = toDayKey(start, timeZone) === toDayKey(end, timeZone);
    if (sameDay) {
      return formatDate(end, { format: "time", timeZone });
    }
    const dateStr = formatDate(end, { format: "weekday-long", timeZone });
    const timeStr = formatDate(end, { format: "time", timeZone });
    return t("billetterie.useEventStatus.dateFinAutreJour", { date: dateStr, heure: timeStr });
  });

  return { isCompleted, isClosed, isPast, isEventEnded, eventEndedLabel, formattedDate, formattedEnd };
}
